import { Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import moment from 'moment-timezone';
import { IconDownload, IconEye } from '@nxsys/components/AppIconSvg';
import AppIconButton from '@nxsys/components/AppIconButton';
import AppTagStatus from '@nxsys/components/AppTagStatus';
import { PSR_REVENUE_STATUS } from '@nxsys/constants/psr';
import { TypedMessages } from '@nxsys/hooks/useTypedIntl';
import { PsrBatchRecordType } from '@nxsys/types/models/revenueFilings/psr';
import { defaultPaymentFrequency } from '@nxsys/constants/AppConstants';
import { PaymentFrequencyEnum } from '@nxsys/constants/AppEnums';
import { formatStatus } from '@nxsys/utils';

type Props = {
  handleClickView: (record: PsrBatchRecordType) => void;
  handleClickDownload: (record: PsrBatchRecordType) => void;
  /** The row whose file is being fetched, so only that icon spins. */
  downloadingId?: number | null;
  messages: TypedMessages;
};

/**
 * NX-6140: what one submission's outcome reads as.
 *
 * Deliberately leads with the failures. "297 of 300 accepted" buries the three
 * people whose pay Revenue does not have, and those are the only rows anyone
 * needs to act on - so the pill says "Failed 3 of 300" and is red.
 */
export const describeBatch = (record: PsrBatchRecordType) => {
  const { counts, revenueStatus, payslipCount } = record;
  const sent = counts?.sent ?? payslipCount ?? 0;

  if (revenueStatus === PSR_REVENUE_STATUS.FAILED) {
    return { tone: 'Failed', label: `Rejected - ${sent} not filed` };
  }
  if (revenueStatus === PSR_REVENUE_STATUS.NOT_RECEIVED) {
    return { tone: 'Failed', label: `Not received - ${sent} not filed` };
  }
  // We could not ASK Revenue. The payslips ARE filed - Revenue acknowledged the
  // message - so the label leads with "Sent", not with a failure. It used to say
  // "see certificate", which was a guess: an expired p12 is the common cause in
  // prod, but a timeout or an unreachable Revenue lands here too, and sending
  // someone to check a healthy certificate wastes their time. The real error is
  // shown verbatim in the Error column instead.
  if (revenueStatus === PSR_REVENUE_STATUS.CHECK_FAILED) {
    return { tone: 'Warning', label: `Sent - not confirmed (${sent})` };
  }
  if (revenueStatus === PSR_REVENUE_STATUS.PENDING) {
    return { tone: 'Pending', label: `Pending - ${sent} sent` };
  }
  if (counts?.invalid) {
    return { tone: 'Invalid', label: `Invalid ${counts.invalid} of ${sent}` };
  }
  if (counts?.warned) {
    return { tone: 'Warning', label: `Warning ${counts.warned} of ${sent}` };
  }
  return { tone: 'Active', label: `Active ${sent} of ${sent}` };
};

export const getPSRBatchColumns = ({
  handleClickView,
  handleClickDownload,
  downloadingId,
  messages,
}: Props): ColumnsType<PsrBatchRecordType> => [
  {
    key: 'actions',
    title: messages['table.actions'],
    fixed: 'left',
    align: 'left',
    width: 110,
    render: (record: PsrBatchRecordType) => (
      <ul className="flex items-center justify-center gap-2">
        <li>
          <AppIconButton
            title={messages['common.view']}
            icon={<IconEye />}
            onClick={() => handleClickView(record)}
          />
        </li>
        {/* The listing row has no payslips on it - the list endpoint returns
            counts, not the message - so this fetches the submission first.
            Same three choices and the same bytes as the submission page. */}
        <li
          className={
            downloadingId === record.id ? 'pointer-events-none opacity-40' : ''
          }
        >
          <AppIconButton
            title={messages['common.download']}
            icon={<IconDownload />}
            onClick={() => handleClickDownload(record)}
          />
        </li>
      </ul>
    ),
  },
  {
    key: 'submissionID',
    title: messages['table.submissionID'],
    dataIndex: 'submissionID',
    width: 220,
  },
  {
    // Revenue's key for the pay event itself: employer + tax year + frequency
    // + period. Every submission for one pay week carries the same one, so two
    // rows sharing a reference were filed against the same run - a correction,
    // or a duplicate. It is also what you search ROS by.
    key: 'payrollRunReference',
    title: messages['table.payrollRunReference'],
    dataIndex: 'payrollRunReference',
    width: 340,
    // One line, like every other cell. The reference is long enough to wrap at
    // this width, which made these rows taller than the rest of the table; the
    // tooltip carries the full string when it does not fit.
    ellipsis: true,
    render: (value: string) =>
      value ? <Tooltip title={value}>{value}</Tooltip> : '-',
  },
  {
    /**
     * NX-6140: which KIND of filing this message is, mirroring the Type column
     * on the UK's FPS list.
     *
     * Without it the three are indistinguishable here: an ordinary pay event, a
     * cessation and a reversal all carry a payrollRunReference and a payslip
     * count. Two rows for one week then look like a duplicate when one of them
     * is a leaver, and the only way to tell was to open the request payload.
     *
     * Rows filed before the column existed read NORMAL, which is what they are.
     */
    key: 'psrType',
    title: messages['table.type'],
    dataIndex: 'psrType',
    align: 'center',
    width: 130,
    render: (value: string) => formatStatus(value || 'NORMAL'),
  },
  {
    key: 'status',
    title: messages['table.status'],
    align: 'center',
    width: 200,
    render: (record: PsrBatchRecordType) => {
      const { tone, label } = describeBatch(record);
      const tag = (
        <AppTagStatus className={`tag-popup ${formatStatus(tone)}`}>
          {label}
        </AppTagStatus>
      );
      // The unchecked count is the honest caveat on an "Accepted" pill: those
      // payslips have no verdict, we are not claiming Revenue kept them.
      return record?.counts?.unchecked ? (
        <Tooltip
          title={`${record.counts.unchecked} payslip(s) not yet confirmed by Revenue`}
        >
          {tag}
        </Tooltip>
      ) : (
        tag
      );
    },
  },
  {
    key: 'error',
    title: messages['table.error'],
    width: 260,
    render: (record: PsrBatchRecordType) => {
      // Whatever actually threw, verbatim. Guessing at the cause from the
      // status alone is how "see certificate" ended up on a 60s timeout.
      const raw =
        (record?.response as any)?.checkError ??
        (record?.response as any)?.message ??
        (typeof record?.response === 'string' ? record.response : null);
      if (!raw) return '-';
      const text = String(raw);
      return (
        <Tooltip title={text}>
          <span className="text-red-600">
            {text.length > 60 ? `${text.slice(0, 60)}...` : text}
          </span>
        </Tooltip>
      );
    },
  },
  {
    key: 'payslipCount',
    title: messages['table.employees'],
    align: 'center',
    width: 110,
    render: (record: PsrBatchRecordType) =>
      record?.counts?.sent ?? record?.payslipCount ?? 0,
  },
  {
    key: 'company',
    title: messages['table.company'],
    width: 220,
    render: (record: PsrBatchRecordType) =>
      record?.company?.companyName || '-',
  },
  {
    key: 'taxPeriod',
    title: messages['table.taxPeriod'],
    align: 'center',
    width: 120,
    render: (record: PsrBatchRecordType) => record?.taxPeriod ?? '-',
  },
  {
    key: 'payFrequency',
    title: messages['table.payFrequency'],
    width: 150,
    render: (record: PsrBatchRecordType) =>
      defaultPaymentFrequency.find(
        (item) => item.value === (record?.payFrequency as PaymentFrequencyEnum),
      )?.label ||
      record?.payFrequency ||
      '-',
  },
  {
    key: 'taxYear',
    title: messages['table.taxYear'],
    align: 'center',
    width: 110,
    dataIndex: 'taxYear',
  },
  {
    key: 'createdAt',
    title: messages['table.submittedAt'],
    width: 180,
    render: (record: PsrBatchRecordType) =>
      record?.createdAt
        ? moment(record.createdAt).format('DD/MM/YYYY HH:mm')
        : '-',
  },
  {
    key: 'revenueCheckedAt',
    title: messages['table.lastChecked'],
    width: 180,
    render: (record: PsrBatchRecordType) =>
      record?.revenueCheckedAt
        ? moment(record.revenueCheckedAt).format('DD/MM/YYYY HH:mm')
        : '-',
  },
];
